import { ComponentProps, ReactNode, useState } from 'react';
import ProductsAvailable from '@alltrails/amplitude/enums/ProductsAvailable';
import useUser from '@alltrails/context/hooks/useUser';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import MultiStepModal from '@alltrails/modal/components/Modal/MultiStepModal';
import useMultiStepModal from '@alltrails/modal/hooks/useMultiStepModal';
import CheckoutModalStep from '../../CheckoutModalStep/CheckoutModalStep';
import CheckoutWelcome from '../../CheckoutWelcome/CheckoutWelcome';
import GenericPeakUpsellStep from '../GenericPeakUpsell/GenericPeakUpsellStep';
import styles from './BaseUpsellModal.module.scss';
import BaseUpsellStep from './BaseUpsellStep';
import { UpsellNavigationProvider } from './UpsellNavigationContext';

type Props = {
  caption?: ComponentProps<typeof BaseUpsellStep>['caption'];
  children?: ReactNode;
  headerImage?: ComponentProps<typeof BaseUpsellStep>['headerImage'];
  headerImageV3?: ComponentProps<typeof BaseUpsellStep>['headerImageV3'];
  heading: React.ReactNode;
  isOpen: boolean;
  isFocusPaused?: boolean;
  onCloseRequest: () => void;
  onSuccess?: () => void;
  primaryButtonText?: React.ReactNode;
  primaryButtonVariant?: ComponentProps<typeof BaseUpsellStep>['primaryButtonVariant'];
  promoEligibleHeading: React.ReactNode;
  tallerOnMobile?: boolean;
  upsellTrigger: ComponentProps<typeof BaseUpsellStep>['upsellTrigger'];
  trailId?: number;
  purchaseTier: PurchaseTier;
  productsAvailable: ProductsAvailable;
  skipCheckout?: boolean;
  isCrossPlatform?: boolean;
  onCrossPlatformRequest?: () => void;
};

export default function BaseUpsellModal({
  caption,
  heading,
  promoEligibleHeading,
  headerImage,
  headerImageV3,
  isOpen,
  isFocusPaused = false,
  onCloseRequest,
  onSuccess,
  primaryButtonText,
  primaryButtonVariant,
  children,
  tallerOnMobile = false,
  upsellTrigger,
  trailId,
  purchaseTier,
  productsAvailable,
  skipCheckout = false,
  isCrossPlatform,
  onCrossPlatformRequest
}: Props) {
  const { activeModal, goBack, navigateTo } = useMultiStepModal<'upsell' | 'peakUpsell' | 'checkout' | 'welcome'>('upsell');
  const user = useUser();
  // Set by whichever step enters checkout, so a Peak purchase is not billed as Plus.
  const [checkoutTier, setCheckoutTier] = useState<PurchaseTier>(purchaseTier);

  const isPeakCrossSellEligible =
    purchaseTier === 'plus' &&
    // Neither the downgrade flow nor the app hand-off can complete a Peak purchase, and an existing Peak
    // subscriber has nothing to buy.
    !skipCheckout &&
    !isCrossPlatform &&
    user?.subscriptionTier !== 'peak';

  const navigateFrom = (tier: PurchaseTier) => (modalKey: 'checkout' | 'peakUpsell' | 'upsell') => {
    if (modalKey === 'checkout') {
      setCheckoutTier(tier);
    }

    navigateTo(modalKey);
  };

  return (
    <MultiStepModal
      isFullHeight={activeModal === 'checkout'}
      backgroundClassName={styles.containerOverOsano}
      initialFocus={false}
      isOpen={isOpen}
      isPaused={isFocusPaused || activeModal === 'checkout'} // Pause on checkout so Apple Pay desktop QR closes
      onCloseRequest={onCloseRequest}
      focusTrapOptions={{ clickOutsideDeactivates: false }}
      {...(activeModal !== 'welcome' ? { onBackRequest: goBack } : {})}
      {...(tallerOnMobile || activeModal === 'checkout' || activeModal === 'welcome' ? { modalClassName: styles.taller } : {})}
    >
      {activeModal === 'checkout' && (
        <CheckoutModalStep
          navigateToWelcome={() => navigateTo('welcome')}
          closeModal={onCloseRequest}
          onSuccess={onSuccess}
          purchaseTier={checkoutTier}
          trigger={upsellTrigger}
        />
      )}
      {activeModal === 'upsell' && (
        <BaseUpsellStep
          caption={caption}
          heading={heading}
          headerImage={headerImage}
          headerImageV3={headerImageV3}
          promoEligibleHeading={promoEligibleHeading}
          navigateTo={navigateFrom(purchaseTier)}
          onCloseRequest={onCloseRequest}
          onSuccess={onSuccess}
          primaryButtonText={primaryButtonText}
          primaryButtonVariant={primaryButtonVariant}
          upsellTrigger={upsellTrigger}
          trailId={trailId}
          productType={purchaseTier === 'peak' ? 'peak' : 'plus'}
          productsAvailable={productsAvailable}
          skipCheckout={skipCheckout}
          isCrossPlatform={isCrossPlatform}
          onCrossPlatformRequest={onCrossPlatformRequest}
        >
          {isPeakCrossSellEligible ? (
            <UpsellNavigationProvider navigateToPeakUpsell={() => navigateTo('peakUpsell')}>{children}</UpsellNavigationProvider>
          ) : (
            children
          )}
        </BaseUpsellStep>
      )}
      {activeModal === 'peakUpsell' && (
        <GenericPeakUpsellStep
          navigateTo={navigateFrom('peak')}
          onCloseRequest={onCloseRequest}
          onSuccess={onSuccess}
          upsellTrigger={upsellTrigger}
        />
      )}
      {activeModal === 'welcome' && <CheckoutWelcome closeModal={onCloseRequest} purchaseTier={checkoutTier} />}
    </MultiStepModal>
  );
}
